import classnames from 'classnames';
import * as React from 'react';

import { getTransitionDurationFromElement } from '../../helpers/dom';
import Overlay from '../Overlay';
import BottomSheetBody from './BottomSheetBody';
import BottomSheetHeader from './BottomSheetHeader';
import { BottomSheetProps } from './types';

let stealFocusTimeout: number;
let returnFocusTimeout: number;

export const BottomSheet: React.FC<
	BottomSheetProps & React.HTMLProps<HTMLDivElement>
> = ({ isOpen, options = {}, onClose, children, className }) => {
	const closeCtaRef = React.useRef<HTMLButtonElement>(null);
	const [transitionDuration, setTransitionDuration] = React.useState(-1);
	const { elementToReturnFocusOnClose, ...otherProps } = options;

	React.useEffect(() => {
		function handleKeydown(event: KeyboardEvent) {
			if (event.code === 'Escape' || event.keyCode === 27) {
				onClose();
			}
		}

		document.body.addEventListener('keydown', handleKeydown);

		return () => {
			document.body.removeEventListener('keydown', handleKeydown);
		};
		// We need to remove/add eventlistener each time we recieve a new reference to onClose
	}, [onClose]);

	React.useEffect(() => {
		if (isOpen) {
			clearTimeout(returnFocusTimeout);
			stealFocusTimeout = window.setTimeout(() => {
				if (closeCtaRef.current) {
					closeCtaRef.current.focus();
				}
			}, transitionDuration); // The delay is required to prevent a janky transition. The delay is the same length as the drawer slide CSS transition
		}
		if (!isOpen) {
			clearTimeout(stealFocusTimeout);
			returnFocusTimeout = window.setTimeout(() => {
				if (elementToReturnFocusOnClose) {
					elementToReturnFocusOnClose.focus();
				}
			}, 0);
		}

		return () => {
			clearTimeout(stealFocusTimeout);
			clearTimeout(returnFocusTimeout);
		};
	}, [elementToReturnFocusOnClose, isOpen, transitionDuration]);

	return (
		<div
			ref={(node) => {
				if (node) {
					if (isOpen) {
						node.removeAttribute('inert');
						(node as HTMLDivElement).removeAttribute('hidden');
					} else {
						node.setAttribute('inert', '');
						setTimeout(() => {
							(node as HTMLDivElement).setAttribute('hidden', 'true');
						}, transitionDuration);
					}
				}
			}}
			data-testid="chatbot-bottom-sheet"
			className={classnames('absolute inset-0', className)}
			hidden
			role="dialog"
			{...otherProps}
		>
			<div
				className={classnames(
					'h-full flex items-end max-h-full absolute bottom-0 left-0 right-0 z-20 p-4'
				)}
			>
				<Overlay
					data-testid="chatbot-bottom-sheet-overlay"
					bgColor="#011F44" // this translates to the color: "water" in Strata
					opacity={50}
					className="z-10"
					onClick={onClose}
				/>

				<div
					ref={(node) => {
						if (node) {
							if (transitionDuration === -1) {
								const duration = getTransitionDurationFromElement(node);

								setTransitionDuration(duration ? duration : 0);
							}
							if (isOpen) {
								setTimeout(() => {
									(node as HTMLDivElement).classList.add(
										'c-chatbot-bottom-sheet-card--open'
									);
								}, 0);
							} else {
								(node as HTMLDivElement).classList.remove(
									'c-chatbot-bottom-sheet-card--open'
								);
							}
						}
					}}
					className="c-chatbot-bottom-sheet-card relative z-20 flex flex-col w-full max-h-full overflow-auto bg-white rounded"
				>
					<BottomSheetHeader onClose={onClose} ref={closeCtaRef} />
					<BottomSheetBody>{children}</BottomSheetBody>
				</div>
			</div>
		</div>
	);
};

export default BottomSheet;
